import type { ReactNode } from 'react';
import {
  BuilderSection,
  CheckboxRow,
  IssueList,
  Labelled,
  NumberInput,
  SegmentedControl,
  Worked,
  inputClass,
} from '../points/controls';
import { SalaryBandsEditor } from './SalaryBandsEditor';
import { SalaryInputsEditor } from './SalaryInputsEditor';
import { floorBandSalary, type SalaryDefinition } from './salaryDefinition';
import { salaryIssuesFor, type SalaryValidation } from './salaryValidate';
import { TARGET_POOL_MAXIMUM, TARGET_POOL_MINIMUM, formatSalary } from './salaryWording';

/**
 * §9.1 — the salary model designer.
 *
 * <b>Two panes: the model on the left, the pool it prices on the right.</b> The preview is not a
 * panel bolted on; it is half of what the screen is for. Nobody can judge a set of price bands by
 * reading them — what they can judge is the list of their own players with prices against their
 * names, and whether the pool is a size that makes a contest worth entering.
 *
 * The sections run in a sequence and are numbered, because an operator meeting this for the first
 * time is following one: what it is, what history it reads, what decides a price, what each slice
 * of the pool costs, who is draftable at all, and what happens to somebody the model has never
 * seen.
 *
 * <b>This component is presentational.</b> It holds no draft, fetches nothing and saves nothing —
 * the app above it owns the definition, the preview call and the buttons in `header`/`footer`. Same
 * split as `PointsSystemBuilder`, and for the same reason: it is what lets one builder serve two
 * catalogues and two visual directions.
 */
export function SalaryModelBuilder({
  value,
  onChange,
  validation,
  readOnly = false,
  readOnlyNotice,
  leaderboards,
  preview,
  previewClassName = 'lg:sticky lg:top-0 lg:max-h-[100dvh]',
  header,
  footer,
}: {
  value: SalaryDefinition;
  onChange: (next: SalaryDefinition) => void;
  validation: SalaryValidation;
  /** The **model** is frozen — every control is read-only and the notice says why. */
  readOnly?: boolean;
  readOnlyNotice?: ReactNode;
  /**
   * The room's leaderboards, for naming a lookback outright. Declared structurally, exactly as
   * `Rundown` and `Ticker` already do it — `@brush/ui` depends on React and nothing else, so the
   * app passes its DTOs straight in.
   */
  leaderboards: readonly { id: string; name: string; status: string }[];
  /** The pool pane. Omitted renders the model full width rather than an empty column. */
  preview?: ReactNode;
  /** Sizing for the sticky pane, since the two apps' shells scroll differently. */
  previewClassName?: string;
  header?: ReactNode;
  footer?: ReactNode;
}) {
  const set = <K extends keyof SalaryDefinition>(key: K, next: SalaryDefinition[K]) =>
    onChange({ ...value, [key]: next });

  const namedBoards = value.lookback.leaderboardIds;
  const usingNamed = namedBoards.length > 0;
  const floor = floorBandSalary(value);

  return (
    <div>
      {header}
      {readOnly && readOnlyNotice ? (
        <div className="mb-4 border border-line border-l-4 border-l-warn bg-surface p-3 text-[12.5px] text-ink-900">
          {readOnlyNotice}
        </div>
      ) : null}

      <div
        className={
          preview
            ? 'grid items-start gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]'
            : 'grid items-start gap-6'
        }
      >
        <div className="min-w-0 space-y-6 border border-line bg-surface p-5">
          <BuilderSection step={1} title="What this model is">
            <div className="space-y-3">
              <Labelled label="Name">
                <input
                  type="text"
                  value={value.name}
                  disabled={readOnly}
                  maxLength={120}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="Summer Series pricing"
                  className={inputClass}
                />
              </Labelled>
              <Labelled
                label="Description"
                hint="Optional. What it does in a line — this is what somebody reads a year from now."
              >
                <input
                  type="text"
                  value={value.description ?? ''}
                  disabled={readOnly}
                  maxLength={500}
                  onChange={(e) => set('description', e.target.value === '' ? null : e.target.value)}
                  placeholder="Mostly how often somebody cashes, with points and money behind it"
                  className={inputClass}
                />
              </Labelled>
              <IssueList issues={salaryIssuesFor(validation, 'identity')} />
            </div>
          </BuilderSection>

          <BuilderSection
            step={2}
            title="Which past series it prices from"
            summary="Prices come from what players have already done here. Nothing else — a model never reads another room's results."
            action={
              <SegmentedControl
                ariaLabel="How the past series are chosen"
                value={usingNamed ? 'named' : 'recent'}
                onChange={(mode) =>
                  set('lookback', {
                    ...value.lookback,
                    leaderboardIds: mode === 'named' ? [leaderboards[0]?.id ?? ''].filter(Boolean) : [],
                  })
                }
                options={[
                  { value: 'recent', label: 'Most recent', disabled: readOnly },
                  {
                    value: 'named',
                    label: 'Pick them',
                    disabled: readOnly || leaderboards.length === 0,
                    title:
                      leaderboards.length === 0
                        ? 'This room has no leaderboards to pick from yet.'
                        : undefined,
                  },
                ]}
              />
            }
          >
            <div className="space-y-3">
              {usingNamed ? (
                <>
                  <div className="max-h-56 space-y-1.5 overflow-y-auto border border-line bg-ground/40 p-3">
                    {leaderboards.map((board) => (
                      <CheckboxRow
                        key={board.id}
                        checked={namedBoards.includes(board.id)}
                        disabled={readOnly}
                        label={board.name}
                        detail={board.status === 'closed' ? undefined : `${board.status} — still moving`}
                        onChange={(on) =>
                          set('lookback', {
                            ...value.lookback,
                            leaderboardIds: on
                              ? [...namedBoards, board.id]
                              : namedBoards.filter((id) => id !== board.id),
                          })
                        }
                      />
                    ))}
                  </div>
                  <Worked>
                    A series that has not closed is still taking results, so prices built from it
                    will move. Closed series are the safe ones to price from.
                  </Worked>
                </>
              ) : (
                <>
                  <Labelled
                    label="How many recent series"
                    className="w-40"
                    hint="Counting back from the most recent closed one."
                  >
                    <NumberInput
                      value={value.lookback.seriesCount}
                      onChange={(seriesCount) => set('lookback', { ...value.lookback, seriesCount })}
                      ariaLabel="How many recent series to price from"
                      min={1}
                      step={1}
                      disabled={readOnly}
                      className="w-full text-right"
                    />
                  </Labelled>
                  <Worked>
                    {value.lookback.seriesCount === 1
                      ? 'The last series you closed. Prices follow your room automatically — every new series reprices the pool.'
                      : `The last ${value.lookback.seriesCount ?? '—'} series you closed, taken together. A longer window steadies the prices and is slower to notice somebody new.`}
                  </Worked>
                </>
              )}
              <IssueList issues={salaryIssuesFor(validation, 'lookback')} />
            </div>
          </BuilderSection>

          <BuilderSection
            step={3}
            title="What decides a price"
            summary="Each of these is compared across the whole pool, so it is where somebody stands that counts, not the raw figure."
          >
            <SalaryInputsEditor
              inputs={value.inputs}
              onChange={(inputs) => set('inputs', inputs)}
              validation={validation}
              readOnly={readOnly}
            />
          </BuilderSection>

          <BuilderSection
            step={4}
            title="What each part of the pool costs"
            summary="Bands run weakest first. The bottom one is the floor, and most of any pool will sit in it."
          >
            <SalaryBandsEditor
              bands={value.mapping.bands}
              onChange={(bands) => set('mapping', { type: 'percentileBands', bands })}
              rounding={value.rounding}
              onRoundingChange={(rounding) => set('rounding', rounding)}
              validation={validation}
              readOnly={readOnly}
            />
          </BuilderSection>

          <BuilderSection
            step={5}
            title="Who can be drafted at all"
            summary="The single most important setting on this screen — it decides whether a contest is a contest."
          >
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4">
                <Labelled
                  label="Cashes needed"
                  className="w-36"
                  hint="Blank means no minimum."
                >
                  <NumberInput
                    value={value.qualification.minCashes}
                    onChange={(minCashes) =>
                      set('qualification', { ...value.qualification, minCashes })
                    }
                    ariaLabel="Minimum cashes to be draftable"
                    min={0}
                    step={1}
                    disabled={readOnly}
                    placeholder="Any"
                    className="w-full text-right"
                  />
                </Labelled>
                <Labelled label="Events needed" className="w-36" hint="Blank means no minimum.">
                  <NumberInput
                    value={value.qualification.minEvents}
                    onChange={(minEvents) =>
                      set('qualification', { ...value.qualification, minEvents })
                    }
                    ariaLabel="Minimum events entered to be draftable"
                    min={0}
                    step={1}
                    disabled={readOnly}
                    placeholder="Any"
                    className="w-full text-right"
                  />
                </Labelled>
                <Labelled
                  label="Most players in the pool"
                  className="w-44"
                  hint={`Aim for ${TARGET_POOL_MINIMUM}–${TARGET_POOL_MAXIMUM}.`}
                >
                  <NumberInput
                    value={value.qualification.maxPoolSize}
                    onChange={(maxPoolSize) =>
                      set('qualification', { ...value.qualification, maxPoolSize })
                    }
                    ariaLabel="Largest draftable pool"
                    min={1}
                    step={10}
                    disabled={readOnly}
                    placeholder="No cap"
                    className="w-full text-right"
                  />
                </Labelled>
              </div>

              {/* Both boundaries, both explained, because they fail in opposite directions and an
                  operator told only "aim for 100–200" cannot tell which way to move. */}
              <Worked>
                Too few and every entrant builds the same team, so the contest comes down to a coin
                flip. Too many and most rosters fill up with players who never turn up — over the
                reference series, a pool of every prior casher left <strong>45% of entrants on no
                score at all</strong>. The cap keeps the best {TARGET_POOL_MINIMUM}–
                {TARGET_POOL_MAXIMUM} and drops the rest.
              </Worked>

              <IssueList issues={salaryIssuesFor(validation, 'qualification')} />
            </div>
          </BuilderSection>

          <BuilderSection
            step={6}
            title="Players the model has never seen"
            summary="Somebody you add to a pool by hand, or whose only appearances fell outside the series above."
          >
            <div className="space-y-4">
              <div className="flex flex-wrap items-end gap-4">
                <Labelled label="They cost" className="w-32">
                  <NumberInput
                    value={value.unpriced.salary}
                    onChange={(salary) => set('unpriced', { ...value.unpriced, salary })}
                    ariaLabel="Price for a player with no history"
                    min={0}
                    step={1}
                    disabled={readOnly}
                    className="w-full text-right"
                  />
                </Labelled>
                {floor !== null ? (
                  <p className="pb-2 text-[11.5px] text-ink-800/55">
                    Your floor band costs <span className="num">{formatSalary(floor)}</span>.
                  </p>
                ) : null}
              </div>

              <CheckboxRow
                checked={value.unpriced.flagAsUnproven}
                disabled={readOnly}
                onChange={(flagAsUnproven) => set('unpriced', { ...value.unpriced, flagAsUnproven })}
                label="Mark them as unproven to entrants"
                detail="An entrant sees that the model had nothing to go on, rather than a price with no explanation behind it."
              />

              {/* §4.3's distinction, stated where somebody is deciding the label. */}
              <Worked>
                <strong>Unproven means the model cannot tell them apart, not that they are
                cheap.</strong> Everybody in the floor band is marked the same way for the same
                reason — four cashers in five cashed exactly once, and there is nothing in that to
                separate them with.
              </Worked>

              <IssueList issues={salaryIssuesFor(validation, 'unpriced')} />
            </div>
          </BuilderSection>

          <p className="text-[11px] leading-relaxed text-ink-800/45">
            Prices are budget units, not money. A contest sets its own budget — the reference format
            is eight players and 200 to spend — so what matters is what a player costs{' '}
            <em>relative to</em> the rest of the pool.
          </p>
        </div>

        {preview ? (
          <div
            className={`flex min-w-0 flex-col border border-line bg-surface ${previewClassName}`}
          >
            {preview}
          </div>
        ) : null}
      </div>

      {footer}
    </div>
  );
}
